import { useInfiniteQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react'
import { useMemo, useState } from 'react'

import { promotionOptionQueryKey } from './promotion-query-keys'

import { Button } from '@/app/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/app/components/ui/command'
import { Input } from '@/app/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/app/components/ui/popover'
import { promotionService, type PromotionOption } from '@/app/services/promotion.service'
import { useDebounce } from '@/hooks/useDebounce'


interface PromotionOptionSelectProps {
  kind: 'products' | 'sucursals' | 'categories' | 'departments'
  selected: PromotionOption[]
  onChange: (items: PromotionOption[]) => void
  placeholder: string
  multiple?: boolean
  disabled?: boolean
  showSelectedList?: boolean
  allowSelectedFilter?: boolean
}

export function PromotionOptionSelect({
  kind,
  selected,
  onChange,
  placeholder,
  multiple = true,
  disabled = false,
  showSelectedList = true,
  allowSelectedFilter = false,
}: PromotionOptionSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedOnly, setSelectedOnly] = useState(false)
  const debouncedSearch = useDebounce(search, 250)
  const query = useInfiniteQuery({
    queryKey: [...promotionOptionQueryKey, kind, debouncedSearch],
    queryFn: ({ pageParam }) => {
      const params = { search: debouncedSearch || undefined, page: pageParam, per_page: 20 }
      if (kind === 'products') return promotionService.productOptions(params)
      if (kind === 'sucursals') return promotionService.sucursalOptions(params)
      if (kind === 'categories') return promotionService.categoryOptions(params)
      return promotionService.departmentOptions(params)
    },
    initialPageParam: 1,
    enabled: open,
    getNextPageParam: (lastPage) => lastPage.current_page < lastPage.last_page
      ? lastPage.current_page + 1
      : undefined,
  })
  const selectedIds = useMemo(() => new Set(selected.map((item) => item.id)), [selected])
  const items = query.data?.pages.flatMap((page) => page.data) ?? []
  const normalizedSearch = search.trim().toLocaleLowerCase('es-MX')
  const displayedItems = selectedOnly
    ? selected.filter((item) => [item.name, item.sku, item.barcode]
      .some((value) => value?.toLocaleLowerCase('es-MX').includes(normalizedSearch)))
    : items

  const choose = (item: PromotionOption) => {
    if (multiple) {
      onChange(selectedIds.has(item.id)
        ? selected.filter((current) => current.id !== item.id)
        : [...selected, item])
      return
    }

    onChange([item])
    setOpen(false)
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            disabled={disabled}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">
              {!multiple && selected[0] ? selected[0].name : placeholder}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
          <Command shouldFilter={false}>
            <div className="flex items-center border-b px-3">
              <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar..."
                className="h-10 border-0 px-0 shadow-none focus-visible:ring-0"
              />
            </div>
            {allowSelectedFilter && (
              <div className="border-b p-2">
                <Button
                  type="button"
                  variant={selectedOnly ? 'secondary' : 'ghost'}
                  size="sm"
                  className="w-full justify-start"
                  onClick={() => setSelectedOnly((current) => !current)}
                >
                  <Check className={selectedOnly ? 'mr-2 h-4 w-4 opacity-100' : 'mr-2 h-4 w-4 opacity-40'} />
                  Solo seleccionados ({selected.length})
                </Button>
              </div>
            )}
            <CommandList>
              {!selectedOnly && query.isLoading && (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando...
                </div>
              )}
              {!selectedOnly && !query.isLoading && query.isError && (
                <p className="py-6 text-center text-sm text-destructive">No fue posible cargar las opciones.</p>
              )}
              {(selectedOnly || (!query.isLoading && !query.isError)) && displayedItems.length === 0 && (
                <CommandEmpty>No se encontraron resultados.</CommandEmpty>
              )}
              <CommandGroup>
                {displayedItems.map((item) => (
                  <CommandItem key={item.id} value={String(item.id)} onSelect={() => choose(item)}>
                    <Check className={selectedIds.has(item.id) ? 'opacity-100' : 'opacity-0'} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{item.name}</p>
                      {(item.sku || item.barcode) && (
                        <p className="truncate text-xs text-muted-foreground">{item.sku || item.barcode}</p>
                      )}
                    </div>
                    {item.is_active === false && <span className="text-xs text-muted-foreground">Inactivo</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
              {!selectedOnly && query.hasNextPage && (
                <div className="p-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full"
                    disabled={query.isFetchingNextPage}
                    onClick={() => void query.fetchNextPage()}
                  >
                    {query.isFetchingNextPage ? 'Cargando...' : 'Cargar más'}
                  </Button>
                </div>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {showSelectedList && selected.length > 0 && (
        <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
          {selected.map((item) => (
            <span key={item.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs text-primary">
              <span className="max-w-52 truncate">{item.name}</span>
              <button
                type="button"
                aria-label={`Quitar ${item.name}`}
                disabled={disabled}
                onClick={() => onChange(selected.filter((current) => current.id !== item.id))}
                className="rounded-full p-0.5 hover:bg-primary/15 disabled:opacity-50"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
